import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { ActionButton, Callout, DirectionalHint } from '@fluentui/react';
import { createUseStyles } from 'react-jss';
import { DailyPlannerContext } from './DailyPlannerContext';
import { TodoEditor, ITodoEditorProps } from './TodoEditor';
import { PlannerIcon } from './PlannerIcon';
import { MARKDOWN_PREFIX, TITLE } from './common';
import { PlaygroundTheme } from '../../Theme';
import { useIsVivara } from '../../hooks/useIsVivara';

interface IDailyPlannerProps {
  onSend: ITodoEditorProps['onSend'];
  sendButtonDisabled: ITodoEditorProps['sendButtonDisabled'];
  conversationId: ITodoEditorProps['conversationId'];
}

const useStyles = createUseStyles<string, unknown, PlaygroundTheme>((theme) => ({
  button: {
    '&:hover, &:active': {
      '& svg path': {
        fill: theme.themeLighter,
      },
    },
  },
  callout: {
    'box-shadow': '0px 4px 8px 0px #00000024',
    borderRadius: 8,
    padding: 8,
    background: theme.themeLight,
    '& .ms-Callout-main': {
      width: 400,
      height: 369,
      display: 'flex',
      flexDirection: 'column',
      background: theme.themeLight,
    },
    '&.is-vivara': {
      background: theme.themeLighterAlt,
      '& .ms-Callout-main': {
        background: theme.themeLighterAlt,
      },
    },
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    margin: '0 0 6px 0',
    fontSize: 16,
    fontWeight: 600,
    color: theme.themePrimary,
    '& .planner-icon': {
      marginRight: 6,
      marginTop: 4,
      width: 18,
      height: 18,
      '& path': {
        fill: `${theme.themePrimary} !important`,
      },
    },
    '& p': {
      fontSize: 12,
      lineHeight: '16px',
    },
    '& span': {
      lineHeight: '20px',
    },
  },
  todoEditor: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    '& .mdxeditor': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      border: '1px solid #D6D6D6',
      borderRadius: 4,
      '& .mdxeditor-toolbar': {
        display: 'none',
      },
      '& .mdxeditor-toolbar, & .mdxeditor-root-contenteditable': {
        backgroundColor: '#fff',
        fontFamily: 'aptos-serif',
      },
      '& .mdxeditor-root-contenteditable': {
        flex: 1,
        overflow: 'auto',
        '& .editor-input': {
          paddingTop: 0,
        },
        '& li[role=checkbox][aria-checked=true]:before': {
          backgroundColor: theme.themePrimary,
        },
      },
    },
    '& .bottom-bar': {
      display: 'flex',
      justifyContent: 'flex-end',
    },
  },
}));

export const DailyPlanner: React.FC<IDailyPlannerProps> = ({
  onSend,
  conversationId,
  sendButtonDisabled,
}) => {
  const isVivara = useIsVivara();
  const {
    markdown, setMarkdown, todoEditorOpen, setTodoEditorOpen,
  } = useContext(DailyPlannerContext);
  const classes = useStyles();
  const onTriggerClick = useCallback(() => {
    setTodoEditorOpen(!todoEditorOpen);
  }, [todoEditorOpen, setTodoEditorOpen]);
  const onCalloutDismiss = useCallback(() => {
    setTodoEditorOpen(false);
  }, [setTodoEditorOpen]);
  const onSendWithPrefx = useCallback(async (v: string) => {
    const valueWithTitlePrefix = `${MARKDOWN_PREFIX}${TITLE}${v.replace(MARKDOWN_PREFIX, '')}`;
    await onSend(valueWithTitlePrefix);
  }, [onSend]);

  return (
    <>
      <ActionButton
        // eslint-disable-next-line react/no-unstable-nested-components
        onRenderIcon={() => <PlannerIcon />}
        onClick={onTriggerClick}
        id="todo-trigger"
        className={classes.button}
      />
      {todoEditorOpen && (
        <Callout
          className={classNames(classes.callout, isVivara && 'is-vivara')}
          isBeakVisible={false}
          target=".message-bar.is-vivara"
          onDismiss={onCalloutDismiss}
          directionalHint={DirectionalHint.bottomRightEdge}
          setInitialFocus
        >
          <div>
            <h4 className={classes.title}>
              <PlannerIcon />
              <span>My daily planner</span>
            </h4>
            <p>{'(">>" means started)'}</p>
          </div>
          <TodoEditor
            className={classes.todoEditor}
            markdown={markdown}
            onMarkdownChange={setMarkdown}
            onSend={onSendWithPrefx}
            conversationId={conversationId}
            sendButtonDisabled={sendButtonDisabled}
          />
        </Callout>
      )}
    </>
  );
};
